import {
  IntegrationType,
  type IIntegration,
} from '@principle-theorem/integrations';
import {
  IActivePartnerAppIntegration,
  IApiKey,
  IOrganisationSubscription,
  IPartnerApp,
  IPartnerAppAccessToken,
  IPractice,
  IWebhook,
  OrganisationCollection,
  OrganisationStatus,
  PartnerAppCollection,
  RootCollection,
  type IBrand,
  type IOrganisation,
  type IRole,
  type IUser,
} from '@principle-theorem/principle-core/interfaces';
import {
  all$,
  asColRef,
  asReffable,
  collectionGroupQuery,
  DocumentReference,
  Firestore,
  multiFilter,
  multiSwitchMap,
  shareReplayCold,
  slugify,
  subCollection,
  undeletedQuery,
  where,
  type AtLeast,
  type CollectionReference,
  type IReffable,
  type WithRef,
} from '@principle-theorem/shared';
import { type ITemporaryOrgDataToken } from '@principle-theorem/temporary-tokens';
import { compact, sortBy } from 'lodash';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { OrganisationCache } from './organisation-cache';
import { OrganisationSubscription } from './organisation-subscription';

export class Organisation {
  static init(
    overrides: AtLeast<IOrganisation, 'name' | 'region'>
  ): IOrganisation {
    return {
      status: OrganisationStatus.Pending,
      ...overrides,
      slug: slugify(overrides.name.toLowerCase()),
      deleted: false,
    };
  }

  static col(): CollectionReference<IOrganisation> {
    return asColRef<IOrganisation>(RootCollection.Organisations);
  }

  static storagePath(organisation: IReffable<IOrganisation>): string {
    return organisation.ref.path;
  }

  static all$(): Observable<WithRef<IOrganisation>[]> {
    return all$(this.col()).pipe(
      map((organisations) => sortBy(organisations, ['name'])),
      shareReplayCold()
    );
  }

  static userCol(
    organisation: IReffable<IOrganisation>
  ): CollectionReference<IUser> {
    return subCollection<IUser>(organisation.ref, OrganisationCollection.Users);
  }

  static users$(
    organisation: IReffable<IOrganisation>,
    filterEnabled: boolean = true
  ): Observable<WithRef<IUser>[]> {
    return OrganisationCache.users.byOrganisation.query$(
      {
        organisationRef: organisation.ref,
        filterEnabled,
      },
      Organisation.userCol(organisation),
      ...compact([filterEnabled ? where('isEnabled', '==', true) : undefined])
    );
  }

  static brandCol(
    organisation: IReffable<IOrganisation>
  ): CollectionReference<IBrand> {
    return subCollection<IBrand>(
      organisation.ref,
      OrganisationCollection.Brands
    );
  }

  static brands$(
    organisation: IReffable<IOrganisation>
  ): Observable<WithRef<IBrand>[]> {
    return all$(undeletedQuery(Organisation.brandCol(organisation))).pipe(
      map((brands) => sortBy(brands, ['name']))
    );
  }

  static temporaryTokenCol(
    organisation: IReffable<IOrganisation>
  ): CollectionReference<ITemporaryOrgDataToken> {
    return subCollection<ITemporaryOrgDataToken>(
      organisation.ref,
      OrganisationCollection.TemporaryTokens
    );
  }

  static integrationCol<IntegrationType extends object = object>(
    organisation: IReffable<IOrganisation>
  ): CollectionReference<IIntegration<IntegrationType>> {
    return subCollection<IIntegration<IntegrationType>>(
      organisation.ref,
      OrganisationCollection.Integrations
    );
  }

  static roleCol(
    organisation: IReffable<IOrganisation>
  ): CollectionReference<IRole> {
    return subCollection<IRole>(organisation.ref, OrganisationCollection.Roles);
  }

  static roles$(
    organisation: IReffable<IOrganisation>
  ): Observable<WithRef<IRole>[]> {
    return all$(undeletedQuery(Organisation.roleCol(organisation))).pipe(
      map((roles) => sortBy(roles, ['name']))
    );
  }

  static subscriptionCol(
    organisation: IReffable<IOrganisation>
  ): CollectionReference<IOrganisationSubscription> {
    return subCollection<IOrganisationSubscription>(
      organisation.ref,
      OrganisationCollection.Subscriptions
    );
  }

  static subscriptions$(
    organisation: IReffable<IOrganisation>
  ): Observable<WithRef<IOrganisationSubscription>[]> {
    return all$(Organisation.subscriptionCol(organisation)).pipe(
      multiFilter((subscription) => !subscription.deleted)
    );
  }

  static async getSubscription(
    organisationRef: DocumentReference<IOrganisation>,
    practiceRef?: DocumentReference<IPractice>
  ): Promise<WithRef<IOrganisationSubscription> | undefined> {
    const subscriptions = await Firestore.getDocs(
      Organisation.subscriptionCol(asReffable(organisationRef))
    );

    return OrganisationSubscription.selectForPractice(
      subscriptions,
      practiceRef
    );
  }

  static apiKeyCol(
    organisation: IReffable<IOrganisation>
  ): CollectionReference<IApiKey> {
    return subCollection<IApiKey>(
      organisation.ref,
      OrganisationCollection.ApiKeys
    );
  }

  static apiKeys$(
    organisation: IReffable<IOrganisation>
  ): Observable<WithRef<IApiKey>[]> {
    return all$(undeletedQuery(Organisation.apiKeyCol(organisation)));
  }

  static webhookCol(
    organisation: IReffable<IOrganisation>
  ): CollectionReference<IWebhook> {
    return subCollection<IWebhook>(
      organisation.ref,
      OrganisationCollection.Webhooks
    );
  }

  static webhooks$(
    organisation: IReffable<IOrganisation>
  ): Observable<WithRef<IWebhook>[]> {
    return all$(undeletedQuery(Organisation.webhookCol(organisation)));
  }

  static partnerIntegrations$(
    organisation: IReffable<IOrganisation>
  ): Observable<IActivePartnerAppIntegration[]> {
    return all$(
      collectionGroupQuery<IPartnerAppAccessToken>(
        PartnerAppCollection.AccessTokens,
        where('organisationRef', '==', organisation.ref),
        where('deleted', '==', false)
      )
    ).pipe(
      multiSwitchMap((accessToken) =>
        Firestore.doc$(
          Firestore.getParentDocRef<IPartnerApp>(accessToken.ref)
        ).pipe(
          map((app) => ({
            app,
            accessToken,
          }))
        )
      )
    );
  }
}

export function isTwilioSMSProvider(organisation: IOrganisation): boolean {
  return organisation.integrations?.smsProvider === IntegrationType.Twilio;
}

export function isPodiumSMSProvider(organisation: IOrganisation): boolean {
  return organisation.integrations?.smsProvider === IntegrationType.Podium;
}

export function isTNZSMSProvider(organisation: IOrganisation): boolean {
  return organisation.integrations?.smsProvider === IntegrationType.TNZ;
}

export function isViaApiSMSProvider(organisation: IOrganisation): boolean {
  return organisation.integrations?.smsProvider === IntegrationType.ViaApi;
}
